import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { aiaQueryOptions, type AiaMember } from "@/lib/aia.queries";
import {
  createAiaMember,
  deleteAiaMember,
  updateAiaMember,
} from "@/lib/aia.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, UserRound } from "lucide-react";
import { MediaField } from "@/components/admin/MediaField";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";
import { MEDIA_TARGETS } from "@/lib/media-buckets";

export const Route = createFileRoute("/_authenticated/admin/advisors")({
  component: AiaAdminPage,
});

type AiaFormState = {
  id?: string;
  first_name: string;
  last_name: string;
  suffix: string;
  title: string;
  org: string;
  bio: string;
  photo_url: string;
  linkedin_url: string;
  is_active: boolean;
};

const emptyAiaForm: AiaFormState = {
  first_name: "",
  last_name: "",
  suffix: "",
  title: "",
  org: "",
  bio: "",
  photo_url: "",
  linkedin_url: "",
  is_active: true,
};

function fullName(m: { first_name: string; last_name: string; suffix?: string | null }) {
  const base = `${m.last_name}, ${m.first_name}`.trim();
  const suf = (m.suffix ?? "").trim();
  return suf ? `${base} ${suf}` : base;
}

function AiaAdminPage() {
  const { data: members = [] } = useQuery(aiaQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createAiaMember);
  const update = useServerFn(updateAiaMember);
  const del = useServerFn(deleteAiaMember);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AiaFormState>(emptyAiaForm);
  // True while the MediaField's Upload/Library picker (sibling modal) is up.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const dirty = useDirty(form, open);
  const [q, setQ] = useState("");

  const sorted = useMemo(
    () =>
      [...members].sort(
        (a, b) =>
          a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
      ),
    [members],
  );
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((m) =>
      [fullName(m), m.title, m.org].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [sorted, q]);

  const saveMut = useMutation({
    mutationFn: async (f: AiaFormState) => {
      const payload = {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        suffix: f.suffix.trim(),
        title: f.title,
        org: f.org,
        bio: f.bio,
        photo_url: f.photo_url,
        linkedin_url: f.linkedin_url,
        is_active: f.is_active,
      };
      if (f.id) return update({ data: { ...payload, id: f.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aia_advisory_members"] });
      setOpen(false);
      setForm(emptyAiaForm);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const toggleActiveMut = useMutation({
    mutationFn: (m: AiaMember) =>
      update({
        data: {
          id: m.id,
          first_name: m.first_name,
          last_name: m.last_name,
          suffix: m.suffix ?? "",
          title: m.title ?? "",
          org: m.org ?? "",
          bio: m.bio ?? "",
          photo_url: m.photo_url ?? "",
          linkedin_url: m.linkedin_url ?? "",
          is_active: !m.is_active,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aia_advisory_members"] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aia_advisory_members"] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const openEdit = (m: AiaMember) => {
    setForm({
      id: m.id,
      first_name: m.first_name,
      last_name: m.last_name,
      suffix: m.suffix ?? "",
      title: m.title ?? "",
      org: m.org ?? "",
      bio: m.bio ?? "",
      photo_url: m.photo_url ?? "",
      linkedin_url: m.linkedin_url ?? "",
      is_active: m.is_active,
    });
    setOpen(true);
  };

  const openNew = () => {
    setForm({ ...emptyAiaForm });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>AIA Industry Advisory Board</CardTitle>
          <CardDescription>
            {members.filter((m) => m.is_active).length} active / {members.length} total. Sorted alphabetically by last name.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-64" />
          <Button size="sm" onClick={openNew}>
            <Plus className="mr-1 h-4 w-4" /> Add advisor
          </Button>
          <FormDialog
            open={open}
            onOpenChange={setOpen}
            size="md"
            icon={UserRound}
            title={form.id ? "Edit AIA advisor" : "Add AIA advisor"}
            context={form.id ? fullName(form) : "New advisor"}
            dirty={dirty}
            busy={libraryOpen || saveMut.isPending}
            asForm
            onSubmit={() => saveMut.mutate(form)}
            footer={
              <>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <SaveButton dirty={dirty} pending={saveMut.isPending} />
              </>
            }
          >
            <div className="space-y-4">
              <FormSection title="Identity">
                <Field label="First name" required>
                  <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </Field>
                <Field label="Last name" required>
                  <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </Field>
                <Field label="Suffix" full>
                  <Input value={form.suffix} onChange={(e) => setForm({ ...form, suffix: e.target.value })} placeholder="MD, PhD, MD FACS…" />
                </Field>
                <Field label="Title">
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Founder & CEO" />
                </Field>
                <Field label="Organization">
                  <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
                </Field>
              </FormSection>
              <FormSection title="Profile">
                <div className="sm:col-span-2">
                  <MediaField
                    label="Photo"
                    value={form.photo_url}
                    target={MEDIA_TARGETS.advisorPhoto}
                    onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))}
                    onPickerOpenChange={setLibraryOpen}
                  />
                </div>
                <Field label="LinkedIn URL" full>
                  <Input type="url" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
                </Field>
                <Field label="Bio" full>
                  <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
                </Field>
              </FormSection>
              <FormSection title="Visibility" cols={1}>
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                  <Switch
                    id="aia-is-active"
                    checked={form.is_active}
                    onCheckedChange={(v: boolean) => setForm({ ...form, is_active: v })}
                  />
                  <Label htmlFor="aia-is-active" className="cursor-pointer">
                    {form.is_active ? "Active — shown on public board" : "Inactive — hidden from public board"}
                  </Label>
                </div>
              </FormSection>
            </div>
          </FormDialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-[700px] overflow-auto rounded-md border">
          <table className="w-full caption-bottom text-sm">
            <thead className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="border-b">
                <th className="h-10 w-12 px-2 text-left align-middle font-medium text-muted-foreground"></th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Name</th>
                <th className="hidden h-10 px-2 text-left align-middle font-medium text-muted-foreground md:table-cell">Title</th>
                <th className="hidden h-10 px-2 text-left align-middle font-medium text-muted-foreground lg:table-cell">Org</th>
                <th className="h-10 w-20 px-2 text-center align-middle font-medium text-muted-foreground">Active</th>
                <th className="h-10 w-28 px-2 text-right align-middle font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filtered.map((m) => (
                <tr key={m.id} className={`border-b transition-colors hover:bg-muted/50 ${m.is_active ? "" : "opacity-60"}`}>
                  <td className="p-2 align-middle">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={fullName(m)} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-muted" />
                    )}
                  </td>
                  <td className="p-2 align-middle font-medium">{fullName(m)}</td>
                  <td className="hidden p-2 align-middle text-muted-foreground md:table-cell">{m.title}</td>
                  <td className="hidden p-2 align-middle text-muted-foreground lg:table-cell">{m.org}</td>
                  <td className="p-2 text-center align-middle">
                    <Switch
                      checked={m.is_active}
                      disabled={toggleActiveMut.isPending}
                      onCheckedChange={() => toggleActiveMut.mutate(m)}
                      aria-label={m.is_active ? "Deactivate" : "Activate"}
                    />
                  </td>
                  <td className="p-2 text-right align-middle">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(m)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm(`Remove ${fullName(m)}?`)) delMut.mutate(m.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-muted-foreground">
                    No advisors match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

